import React, { forwardRef, useCallback, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactTable } from '@components';
import { MRT_ToggleGlobalFilterButton } from 'material-react-table';
import { apiDeleteMEVData, apiGetPortfolio } from '@store';
import { useDispatch } from 'react-redux';
import { showToast } from '@utils';

const PortfolioDetails = forwardRef(({ handleClose, singlePortfolio }, ref) => {
    const dispatch = useDispatch();

    const handleDeleteMEV = useCallback(
        async (row) => {
            if (window.confirm('Are you sure you want to delete this MEV?')) {
                const resp = await dispatch(
                    apiDeleteMEVData({
                        portfoliouuid: singlePortfolio.uuid,
                        mevname: row.original?.name
                    })
                );
                if (resp.meta.requestStatus === 'fulfilled') {
                    dispatch(apiGetPortfolio());
                    showToast('success', 'MEV deleted successfully');
                } else if (resp.meta.requestStatus === 'rejected') {
                    showToast('Failed to delete MEV', 'error');
                }
            }
        },
        [dispatch, singlePortfolio]
    );

    // column for Effective Monthly Volume
    const columnsEMV = useMemo(
        () => [
            {
                header: 'Name',
                id: 'name',
                accessorFn: ({ name }) => name || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            },
            {
                header: 'Filename',
                id: 'filename',
                accessorFn: ({ filename }) => filename || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            },
            {
                header: 'Year',
                id: 'year',
                accessorFn: ({ year }) => year || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            },
            {
                header: 'Month',
                id: 'month',
                accessorFn: ({ month }) => month || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            }
        ],
        []
    );

    // actions menu for Effective Monthly Volume
    const actionsMenuEMV = useMemo(
        () =>
            ({ row }) => [
                <Button
                    variant="outlined"
                    color="error"
                    sx={{
                        p: 0,
                        m: 0,
                        borderColor: 'brown',
                        color: 'brown',
                        fontSize: '13px',
                        '&:hover': {
                            borderColor: 'brown',
                            color: 'white',
                            backgroundColor: 'brown'
                        }
                    }}
                    onClick={() => handleDeleteMEV(row)}
                >
                    Delete
                </Button>
            ],
        [handleDeleteMEV]
    );

    // column for Demand Files
    const columnsDF = useMemo(
        () => [
            {
                header: 'Name',
                id: 'data_type',
                accessorFn: ({ data_type }) => data_type || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            },
            {
                header: 'Filename',
                id: 'profile_name',
                accessorFn: ({ profile_name }) => profile_name || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            }
        ],
        []
    );

    return (
        <Box className="modal-container">
            <Box className="flex justify-between modal-header">
                <h2 className="modal-title">Portfolio Details</h2>
                <CloseIcon className="cursor-pointer" onClick={handleClose} />
            </Box>
            <Box className="modal-body" display={'flex'} gap={3}>
                <Box display={'flex'} flexDirection={'column'} gap={3}>
                    <Box>
                        <Box display={'flex'} gap={2} alignItems={'center'}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Name:
                            </Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {singlePortfolio.name}
                            </Typography>
                        </Box>
                        {/* <Box display={'flex'} gap={2} alignItems={'center'}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Owner:
                            </Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {singlePortfolio.owner}
                            </Typography>
                        </Box> */}
                        <Box display={'flex'} gap={2} alignItems={'center'}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Commodity:
                            </Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {singlePortfolio.commodity}
                            </Typography>
                        </Box>
                    </Box>
                    <ReactTable
                        renderToolbarInternalActions={({ table }) => (
                            <div onMouseDown={(e) => e.stopPropagation()}>
                                <MRT_ToggleGlobalFilterButton table={table} />
                            </div>
                        )}
                        headerText={'Effective Monthly Volume'}
                        columns={columnsEMV}
                        data={singlePortfolio.mev_data || []}
                        actions={actionsMenuEMV}
                        isLoading={false}
                    />
                </Box>
                <Box display={'flex'} flexDirection={'column'} gap={3}>
                    <Box>
                        <Box display={'flex'} gap={2} alignItems={'center'}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Currency:
                            </Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {singlePortfolio.currency}
                            </Typography>
                        </Box>
                        <Box display={'flex'} gap={2} alignItems={'center'}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Location:
                            </Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {singlePortfolio.location}
                            </Typography>
                        </Box>
                        <Box display={'flex'} gap={2} alignItems={'center'}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Profile csv:
                            </Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {singlePortfolio.profile}
                            </Typography>
                        </Box>
                    </Box>
                    <ReactTable
                        renderToolbarInternalActions={({ table }) => (
                            <div onMouseDown={(e) => e.stopPropagation()}>
                                <MRT_ToggleGlobalFilterButton table={table} />
                            </div>
                        )}
                        headerText={'Demand Files'}
                        columns={columnsDF}
                        data={singlePortfolio.demandfiles || []}
                        actionsMenu={actionsMenuEMV}
                        isLoading={false}
                    />
                </Box>
            </Box>
            <Box className="modal-footer">
                <Button
                    className="modal-button-secondary"
                    onClick={handleClose}
                >
                    Close
                </Button>
            </Box>
        </Box>
    );
});

export default PortfolioDetails;
